/**
 * Home2 Compiler Steps Component
 * Tabbed infographic showing the 4 compiler steps.
 * 
 * @module components/home2/Home2Compiler
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { BookOpen, Palette, Code, Download, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { COMPILER_STEPS } from '../../data/home2Content';

const iconMap = {
  library: BookOpen,
  palette: Palette,
  code: Code,
  download: Download,
};

const Home2Compiler = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [expandedDetails, setExpandedDetails] = useState(false);

  const currentStep = COMPILER_STEPS[activeStep];
  const Icon = iconMap[currentStep.icon];

  return (
    <section className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-8">
          How TweePorter Works
        </h2>
        
        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 mb-8 overflow-x-auto">
          {COMPILER_STEPS.map((step, index) => {
            const StepIcon = iconMap[step.icon];
            const isActive = index === activeStep;
            return (
              <button
                key={step.id}
                onClick={() => {
                  setActiveStep(index);
                  setExpandedDetails(false);
                }}
                className={`flex-1 min-w-[100px] flex flex-col items-center py-4 px-2 border-b-2 transition-all ${
                  isActive 
                    ? 'border-indigo-600 text-indigo-600' 
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <div className={`p-2 rounded-lg mb-2 ${isActive ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                  <StepIcon className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium">Step {step.number}</span>
                <span className="text-sm font-semibold">{step.title}</span>
              </button>
            );
          })}
        </div>
        
        {/* Active Step Content */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 shadow-sm">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Icon className="h-8 w-8 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
                {currentStep.headline}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {currentStep.description}
              </p>
            </div>
          </div>
          
          {/* Expandable Details */}
          <button
            onClick={() => setExpandedDetails(!expandedDetails)}
            className="w-full flex items-center justify-between py-3 px-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors mt-4"
          >
            <span className="font-medium text-slate-700">{currentStep.expandedTitle}</span>
            {expandedDetails ? (
              <ChevronUp className="h-5 w-5 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate-500" />
            )}
          </button>
          
          {expandedDetails && (
            <div className="mt-4 pl-4 border-l-2 border-indigo-200">
              <ul className="space-y-2">
                {currentStep.expandedContent.map((item, index) => (
                  <li key={index} className="text-slate-600 flex items-start gap-2">
                    <span className="text-indigo-500 mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* CTA Button */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <Button
              onClick={() => navigate(currentStep.ctaPath)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {currentStep.cta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home2Compiler;
